import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Inbox as InboxIcon, Paperclip, RefreshCw } from "lucide-react";
import {
  REQUEST_STATUS_LABELS,
  filterToMine,
  requestAttachments,
  requestTypeLabel,
} from "@/lib/portalRequests";
import { useAuth } from "@/lib/AuthContext";
import { usePortalFocus, useScrollToFocus } from "@/hooks/usePortalFocus";

// The work queue: requests raised by the portal tools, in one place, grouped by firm.
//
// This is what replaces working from an inbox. An email is a notification and carries no
// state, so nothing can say whether a request has been picked up, and the firm that raised
// it has no way to find out. These records carry both.
//
// Administrators see every firm and can move a request along. A firm account sees only its
// own, read-only. The API enforces that; this page only reflects it.

const STATUS_ORDER = ["received", "in_progress", "blocked", "completed", "cancelled"];

const STATUS_STYLES = {
  received: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  blocked: "bg-red-50 text-red-700 border-red-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-500 border-slate-200",
};

const PRIORITY_STYLES = {
  urgent: "bg-red-600 text-white",
  high: "bg-orange-500 text-white",
  normal: "bg-slate-200 text-slate-700",
  low: "bg-slate-100 text-slate-500",
};

function formatWhen(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

export default function RequestQueue({ brand = "", title = "Request queue" }) {
  // Read from the session rather than taken as a prop. A route passing isAdmin would be a
  // second place for the answer to live, and the wrong one - the API decides what a caller
  // may see and do, and this only mirrors it so the interface does not offer actions that
  // will be refused.
  const { user } = useAuth();
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  // A supervisor sees their firm's whole workload and can hand work over. Ordinary staff
  // see their own. The server enforces this; the page only reflects it, so that it does
  // not offer a control that would be refused.
  const isSupervisor = isAdmin || role === "supervisor";
  const myEmail = user?.email || "";

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // A request opened from the morning summary: scrolled to and marked once the list is in.
  const focus = usePortalFocus("request");
  useScrollToFocus(focus?.id, !loading);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [brandFilter, setBrandFilter] = useState(brand);
  const [updatingId, setUpdatingId] = useState(null);
  // Someone at a firm opens this to follow their own work, so that is what they land on.
  // The firm's whole queue stays one click away - it was already visible to them, and
  // being able to see it matters when a colleague is away. An administrator works the
  // queue rather than raising it, so this starts wide for them instead.
  const [scope, setScope] = useState(isAdmin ? "firm" : "mine");
  const [reassigning, setReassigning] = useState(null);
  const [assignee, setAssignee] = useState("");

  // Only offer "mine" when the session actually identifies the person. Without an email
  // there is nothing to match on, and defaulting to a filter that can only ever return
  // nothing would look like the queue had lost their work rather than like a filter.
  // Ordinary staff are only ever sent their own requests, so a toggle would be a control
  // that changes nothing. It is offered to those who actually have two views.
  const canFilterToMe = Boolean(myEmail) && isSupervisor;
  const effectiveScope = scope === "mine" && !canFilterToMe ? "firm" : scope;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      // "open" is not a status the API knows. It is the working view - everything not yet
      // finished - which is what someone clearing a queue actually wants to see.
      if (statusFilter !== "open" && statusFilter !== "all") params.set("status", statusFilter);
      if (isAdmin && brandFilter) params.set("brand", brandFilter);

      const response = await fetch("/api/portal-requests?" + params.toString(), {
        credentials: "same-origin",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "The queue could not be loaded (" + response.status + ").");
      }
      setRequests(await response.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "The queue could not be loaded.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, brandFilter, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // Scope first, then status, so the status counts describe the list actually on screen.
  const inScope = useMemo(
    () => (effectiveScope === "mine" ? filterToMine(requests, myEmail) : requests),
    [requests, effectiveScope, myEmail],
  );

  const visible = useMemo(() => {
    if (statusFilter !== "open") return inScope;
    return inScope.filter((r) => r.status !== "completed" && r.status !== "cancelled");
  }, [inScope, statusFilter]);

  const counts = useMemo(() => {
    const tally = {};
    inScope.forEach((r) => {
      tally[r.status] = (tally[r.status] || 0) + 1;
    });
    return tally;
  }, [inScope]);

  const setStatus = async (request, status) => {
    setUpdatingId(request.id);
    try {
      const response = await fetch("/api/portal-requests?id=" + encodeURIComponent(request.id), {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "The request could not be updated.");
      }
      const updated = await response.json();
      setRequests((current) => current.map((r) => (r.id === updated.id ? updated : r)));
      toast.success("Marked " + (REQUEST_STATUS_LABELS[status] || status).toLowerCase() + ".");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "The request could not be updated.");
    } finally {
      setUpdatingId(null);
    }
  };

  const reassign = async (request, email, name) => {
    setUpdatingId(request.id);
    try {
      const response = await fetch("/api/portal-requests?id=" + encodeURIComponent(request.id), {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ assigned_to_email: email, assigned_to_name: name || "" }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || "The request could not be reassigned.");
      }
      const updated = await response.json();
      setRequests((current) => current.map((r) => (r.id === updated.id ? updated : r)));
      setReassigning(null);
      setAssignee("");
      toast.success(email ? "Reassigned to " + email + "." : "Returned to the queue.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "The request could not be reassigned.");
    } finally {
      setUpdatingId(null);
    }
  };

  // Colleagues the portal has already seen, offered as suggestions. There is no directory
  // to read, so this is a convenience and not a restriction: a new lawyer who has never
  // raised a request will not be listed, and the field still accepts any address.
  const knownColleagues = useMemo(() => {
    const seen = new Map();
    requests.forEach((r) => {
      [[r.assigned_to_email, r.assigned_to_name], [r.submitted_by_email, r.submitted_by]]
        .forEach(([email, name]) => {
          const key = String(email || "").trim().toLowerCase();
          if (key && !seen.has(key)) seen.set(key, name || "");
        });
    });
    return [...seen.entries()].map(([email, name]) => ({ email, name })).sort(
      (a, b) => a.email.localeCompare(b.email),
    );
  }, [requests]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <InboxIcon className="w-5 h-5 text-slate-500" /> {title}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {/* This has to describe what is actually on the page. Ordinary staff are sent
                their own work only, and have no toggle, so the firm wording fell through to
                them and promised a view they were not getting. */}
            {!isSupervisor
              ? "Requests you have raised or that have been handed to you."
              : effectiveScope === "mine"
                ? "Requests you have raised or that have been handed to you."
                : isAdmin
                  ? "Work raised from the portal tools, across every firm."
                  : "Every request your firm has raised, and what has been done with them."}
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className={"flex flex-wrap gap-2 mb-3" + (canFilterToMe ? "" : " hidden")}>
        {[
          { value: "mine", label: "Mine" },
          { value: "firm", label: isAdmin ? "Everyone" : "Everyone at the firm" },
        ].map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setScope(option.value)}
            className={
              "rounded-lg border px-3 py-1.5 text-xs font-semibold transition " +
              (effectiveScope === option.value
                ? "border-slate-900 bg-white text-slate-900 ring-1 ring-slate-900"
                : "border-slate-200 text-slate-500 hover:bg-slate-50")
            }
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {["open", ...STATUS_ORDER, "all"].map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatusFilter(value)}
            className={
              "rounded-lg border px-3 py-1.5 text-xs font-semibold transition " +
              (statusFilter === value
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 text-slate-600 hover:bg-slate-50")
            }
          >
            {value === "open" ? "Open" : value === "all" ? "All" : REQUEST_STATUS_LABELS[value]}
            {counts[value] ? " (" + counts[value] + ")" : ""}
          </button>
        ))}
        {isAdmin && (
          <input
            value={brandFilter}
            onChange={(event) => setBrandFilter(event.target.value.trim())}
            placeholder="Filter by firm, e.g. lacw"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs w-44"
          />
        )}
      </div>

      <datalist id="portal-request-colleagues">
        {knownColleagues.map((person) => (
          <option key={person.email} value={person.email}>
            {person.name || person.email}
          </option>
        ))}
      </datalist>

      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-4">
          {error}
        </p>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the queue…
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-16 text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl">
          {statusFilter === "open"
            ? effectiveScope === "mine"
              ? "Nothing outstanding of yours."
              : "Nothing outstanding."
            : "No requests match this filter."}
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((request) => (
            <div
              key={request.id}
              data-focus-id={request.id}
              className="border border-slate-200 rounded-xl p-4 bg-white flex flex-wrap gap-4 items-start justify-between"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={
                      "rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                      (STATUS_STYLES[request.status] || "")
                    }
                  >
                    {REQUEST_STATUS_LABELS[request.status] || request.status}
                  </span>
                  {request.priority !== "normal" && (
                    <span
                      className={
                        "rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide " +
                        (PRIORITY_STYLES[request.priority] || "")
                      }
                    >
                      {request.priority}
                    </span>
                  )}
                  {isAdmin && request.brand && (
                    <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                      {request.brand}
                    </span>
                  )}
                  <span className="text-[11px] text-slate-400">
                    {requestTypeLabel(request.request_type)}
                  </span>
                  {request.payload?.source === "email" && (
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">
                      From email
                    </span>
                  )}
                </div>

                <p className="font-semibold text-slate-800 text-sm mt-1.5 truncate">{request.title}</p>

                {requestAttachments(request).length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {requestAttachments(request).map((attachment) => (
                      <a
                        key={attachment.url}
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        <Paperclip className="h-3 w-3" />
                        {attachment.name || "Attachment"}
                      </a>
                    ))}
                  </div>
                )}

                <p className="text-xs text-slate-500 mt-0.5">
                  {[
                    request.file_number ? "File " + request.file_number : "",
                    request.submitted_by,
                    formatWhen(request.submitted_at),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>

                <p className="text-xs mt-1">
                  {request.assigned_to_email ? (
                    <span className="text-slate-600">
                      With{" "}
                      <span className="font-semibold">
                        {request.assigned_to_name || request.assigned_to_email}
                      </span>
                      {request.assigned_by ? " · handed over by " + request.assigned_by : ""}
                    </span>
                  ) : (
                    // Not an error: it simply has not been handed to anyone, so it still
                    // sits with whoever raised it.
                    <span className="text-slate-400">Unassigned</span>
                  )}
                </p>

                {request.payload?.details && (
                  // The whole request as the tool submitted it (Apply for Aid sends its
                  // worksheet), so it can be worked from here without the email.
                  <details className="mt-1.5">
                    <summary className="cursor-pointer text-xs font-semibold text-slate-600 hover:text-slate-900">
                      Request details
                    </summary>
                    <pre className="mt-1.5 max-h-96 overflow-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 font-sans text-xs text-slate-700">
                      {String(request.payload.details)}
                    </pre>
                  </details>
                )}

                {request.actioned_at && (
                  // What the firm is actually being shown: that it was done, by whom, when.
                  <p className="text-xs text-emerald-700 mt-1">
                    {REQUEST_STATUS_LABELS[request.status]} by {request.actioned_by || "the team"} ·{" "}
                    {formatWhen(request.actioned_at)}
                    {request.action_note ? " — " + request.action_note : ""}
                  </p>
                )}
              </div>

              <div className="flex flex-col items-end gap-1.5">
                {reassigning === request.id ? (
                  <div className="flex flex-wrap items-center gap-1.5 justify-end">
                    <input
                      list="portal-request-colleagues"
                      value={assignee}
                      autoFocus
                      onChange={(event) => setAssignee(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") reassign(request, assignee.trim(), "");
                        if (event.key === "Escape") setReassigning(null);
                      }}
                      placeholder="Email of the lawyer taking it on"
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs w-64"
                    />
                    <button
                      type="button"
                      disabled={updatingId === request.id}
                      onClick={() => reassign(request, assignee.trim(), "")}
                      className="rounded-lg border border-slate-900 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                    >
                      Reassign
                    </button>
                    <button
                      type="button"
                      onClick={() => { setReassigning(null); setAssignee(""); }}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setReassigning(request.id);
                      setAssignee(request.assigned_to_email || "");
                    }}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    {request.assigned_to_email ? "Reassign" : "Assign"}
                  </button>
                )}

              {isAdmin && (
                <div className="flex flex-wrap gap-1.5">
                  {request.status !== "in_progress" && request.status !== "completed" && (
                    <button
                      type="button"
                      disabled={updatingId === request.id}
                      onClick={() => setStatus(request, "in_progress")}
                      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      Start
                    </button>
                  )}
                  {request.status !== "completed" && (
                    <button
                      type="button"
                      disabled={updatingId === request.id}
                      onClick={() => setStatus(request, "completed")}
                      className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      Complete
                    </button>
                  )}
                  {request.status === "completed" && (
                    <button
                      type="button"
                      disabled={updatingId === request.id}
                      onClick={() => setStatus(request, "received")}
                      className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    >
                      Reopen
                    </button>
                  )}
                </div>
              )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
